import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Instagram, Send, MessageCircle, Loader2 } from "lucide-react";
import { CONTACT } from "../mock";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";
import { sendContact } from "../lib/api";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email) {
      toast({ title: "Champs requis", description: "Merci d'indiquer votre prénom et votre e-mail." });
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      setSent(true);
      toast({ title: "Merci pour votre envoi !", description: "Je vous recontacte dans les plus brefs délais." });
      setForm({ firstName: "", lastName: "", email: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast({
        title: "Une erreur est survenue",
        description: "Impossible d'envoyer votre message. Réessayez plus tard.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: Phone, label: "Téléphone", value: CONTACT.phone, href: `tel:${CONTACT.phoneRaw}` },
    { icon: Mail, label: "E-mail", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: MessageCircle, label: "WhatsApp", value: "Écrivez-moi", href: CONTACT.whatsapp },
    { icon: Instagram, label: "Instagram", value: "@bs_import_auto", href: CONTACT.instagram },
  ];

  return (
    <section id="contact" className="section-pad relative bg-[#0b0b0e] border-t border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          {/* Left info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <span className="eyebrow">Contactez-moi</span>
            <h2 className="section-title text-4xl md:text-6xl text-white mt-5">
              Prenons la <span className="racing-text">route</span> ensemble
            </h2>
            <p className="mt-5 text-white/60 text-lg leading-relaxed max-w-md">
              Un projet, une question ou l'envie de vous lancer ? Je vous réponds
              rapidement et vous accompagne à chaque étape.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {contactItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target={item.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="card-lift glass rounded-xl p-5 flex items-center gap-4 group"
                >
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#e11d2a] to-[#ff6a00] flex items-center justify-center flex-shrink-0">
                    <item.icon size={20} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white/45 text-xs uppercase tracking-wide">{item.label}</p>
                    <p className="text-white font-medium truncate">{item.value}</p>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="glass rounded-3xl p-8 md:p-10 border border-white/8"
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-white/70 mb-2 font-medium">Prénom</label>
                  <Input name="firstName" value={form.firstName} onChange={handleChange} placeholder="Votre prénom" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12" />
                </div>
                <div>
                  <label className="block text-sm text-white/70 mb-2 font-medium">Nom de famille</label>
                  <Input name="lastName" value={form.lastName} onChange={handleChange} placeholder="Votre nom" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-2 font-medium">E-mail</label>
                <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="vous@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12" />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-2 font-medium">Message</label>
                <Textarea name="message" value={form.message} onChange={handleChange} placeholder="Décrivez votre projet..." rows={4} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none" />
              </div>
              <button type="submit" disabled={loading} className="btn-racing w-full justify-center disabled:opacity-70">
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                {sent ? "Merci pour votre envoi !" : loading ? "Envoi..." : "Envoyer"}
                {!sent && !loading && <Send size={16} />}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
